/**
 * Upload — CSV ingestion with optional batch tags. On success the server creates
 * the batch + leads (PII encrypted at rest) and enqueues enrichment per lead.
 * Columns auto-map by header name (name, institution, title, email, source,
 * lead_type, engagement_topic).
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiClientError } from '../api/client';

interface UploadResult {
  created: number;
  enqueued: number;
  skipped: number;
  errors: Array<{ row: number; message: string }>;
}

export function Upload(): JSX.Element {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [tagsRaw, setTagsRaw] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UploadResult | null>(null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!file) {
      setError('Please choose a CSV file.');
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    const tags = tagsRaw
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    try {
      const res = await api.uploadCsv(file, tags);
      setResult(res);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="upload-page">
      <div className="page-head">
        <h1>Upload leads</h1>
      </div>

      <form className="card upload-form" onSubmit={onSubmit}>
        <label className="field">
          <span>CSV file</span>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <label className="field">
          <span>Batch tags (comma-separated, optional)</span>
          <input
            type="text"
            value={tagsRaw}
            onChange={(e) => setTagsRaw(e.target.value)}
            placeholder="aacr-2026, west-region"
          />
        </label>
        <p className="muted">
          Expected columns: <code>name</code>, <code>institution</code>,{' '}
          <code>title</code>, <code>email</code>, <code>source</code>,{' '}
          <code>lead_type</code>, <code>engagement_topic</code>. Only{' '}
          <code>name</code> is required; headers auto-map.
        </p>
        {error && <div className="form-error">{error}</div>}
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Uploading…' : 'Upload & enrich'}
        </button>
      </form>

      {result && (
        <div className="card upload-result">
          <h3>Upload complete</h3>
          <ul>
            <li>{result.created} leads created</li>
            <li>{result.enqueued} enrichment jobs enqueued</li>
            <li>{result.skipped} rows skipped</li>
          </ul>
          {result.errors.length > 0 && (
            <details>
              <summary>{result.errors.length} row error(s)</summary>
              <ul className="muted">
                {result.errors.map((e) => (
                  <li key={e.row}>
                    Row {e.row}: {e.message}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate('/')}
          >
            View leads
          </button>
        </div>
      )}
    </div>
  );
}
